<script>
import { mapGetters } from 'vuex';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useConfig } from 'dashboard/composables/useConfig';
import {
  getInboxClassByType,
  getInboxWarningIconClass,
} from 'dashboard/helper/inbox';

import SecondaryChildNavItem from './SecondaryChildNavItem.vue';
import {
  isOnMentionsView,
  isOnUnattendedView,
} from '../../../store/modules/conversations/helpers/actionHelpers';
import Policy from '../../policy.vue';

export default {
  components: { SecondaryChildNavItem, Policy },
  props: {
    menuItem: {
      type: Object,
      default: () => ({}),
    },
    // `toState` del grupo colapsable abierto (acordeón: solo uno a la vez).
    // Lo controla el padre `Secondary.vue`; null = todos plegados.
    openGroup: {
      type: String,
      default: null,
    },
  },
  setup() {
    const { isAdmin } = useAdmin();
    const { isEnterprise } = useConfig();
    return {
      isAdmin,
      isEnterprise,
    };
  },
  data() {
    // «Mostrar más» de una sección con `childLimit` (p. ej. Etiquetas).
    return { showAllChildren: false };
  },
  computed: {
    ...mapGetters({
      activeInbox: 'getSelectedInbox',
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      globalConfig: 'globalConfig/get',
    }),
    isCountZero() {
      return this.menuItem.count === 0;
    },
    isActiveView() {
      return this.computedClass.includes('active-view');
    },
    hasSubMenu() {
      return !!this.menuItem.children;
    },
    // Menú v2: una opción dentro de un grupo respeta su función activa.
    visibleChildren() {
      return (this.menuItem.children || []).filter(
        child =>
          !child.featureFlag ||
          this.isFeatureEnabledonAccount(this.accountId, child.featureFlag)
      );
    },
    // Con `childLimit` se ven los primeros N; los demás con «Mostrar más». La opción
    // abierta en este momento se ve siempre, aunque esté más abajo.
    hasChildLimit() {
      const limit = this.menuItem.childLimit;
      return !!limit && this.visibleChildren.length > limit;
    },
    shownChildren() {
      if (!this.hasChildLimit || this.showAllChildren)
        return this.visibleChildren;
      const limit = this.menuItem.childLimit;
      const current = this.$route && this.$route.path;
      return this.visibleChildren.filter(
        (child, index) => index < limit || child.toState === current
      );
    },
    hiddenChildrenCount() {
      return this.visibleChildren.length - this.shownChildren.length;
    },
    isCollapsibleGroup() {
      return this.hasSubMenu && !!this.menuItem.collapsible;
    },
    isExpanded() {
      return this.openGroup === this.menuItem.toState;
    },
    // feat/bandejas_por_tipo: grupo de bandejas en acordeón. Cuál está abierto lo
    // decide `Secondary.vue` (menuItem.folded); aquí solo se avisa el clic.
    isFoldable() {
      return this.hasSubMenu && !!this.menuItem.foldKey;
    },
    isFolded() {
      return this.isFoldable && !!this.menuItem.folded;
    },
    hasHeaderToggle() {
      return this.isCollapsibleGroup || this.isFoldable;
    },
    headerChevron() {
      const open = this.isFoldable ? !this.isFolded : this.isExpanded;
      return open ? 'chevron-down' : 'chevron-right';
    },
    hasLabelCount() {
      return Number.isInteger(this.menuItem.labelCount);
    },
    areChildrenVisible() {
      if (this.isFoldable) return !this.isFolded;
      return !this.isCollapsibleGroup || this.isExpanded;
    },
    isMenuItemVisible() {
      if (this.menuItem.globalConfigFlag) {
        // this checks for the `csmlEditorHost` flag in the global config
        // if this is present, we toggle the CSML editor menu item
        // TODO: This is very specific, and can be handled better, fix it
        return !!this.globalConfig[this.menuItem.globalConfigFlag];
      }

      let isFeatureEnabled = true;
      if (this.menuItem.featureFlag) {
        isFeatureEnabled = this.isFeatureEnabledonAccount(
          this.accountId,
          this.menuItem.featureFlag
        );
      }

      if (this.menuItem.isEnterpriseOnly) {
        if (!this.isEnterprise) return false;
        return isFeatureEnabled || this.globalConfig.displayManifest;
      }

      if (this.menuItem.featureFlag) {
        return this.isFeatureEnabledonAccount(
          this.accountId,
          this.menuItem.featureFlag
        );
      }

      return isFeatureEnabled;
    },
    isAllConversations() {
      return (
        this.$store.state.route.name === 'inbox_conversation' &&
        this.menuItem.toStateName === 'home'
      );
    },
    isMentions() {
      return (
        isOnMentionsView({ route: this.$route }) &&
        this.menuItem.toStateName === 'conversation_mentions'
      );
    },
    isUnattended() {
      return (
        isOnUnattendedView({ route: this.$route }) &&
        this.menuItem.toStateName === 'conversation_unattended'
      );
    },
    isTeamsSettings() {
      return (
        this.$store.state.route.name === 'settings_teams_edit' &&
        this.menuItem.toStateName === 'settings_teams_list'
      );
    },
    isInboxSettings() {
      return (
        this.$store.state.route.name === 'settings_inbox_show' &&
        this.menuItem.toStateName === 'settings_inbox_list'
      );
    },
    isIntegrationsSettings() {
      return (
        this.$store.state.route.name === 'settings_integrations_webhook' &&
        this.menuItem.toStateName === 'settings_integrations'
      );
    },
    isApplicationsSettings() {
      return (
        this.$store.state.route.name === 'settings_applications_integration' &&
        this.menuItem.toStateName === 'settings_applications'
      );
    },
    isCurrentRoute() {
      const name = this.$store.state.route.name || '';
      // Menú v2: una opción que abre varias vistas (Tablero | Lista | Embudo) se
      // marca activa en cualquiera de ellas, y también en la suya (p. ej. la lista de
      // Organizaciones además de la página de cada una).
      if (
        this.menuItem.activeRoutes &&
        this.menuItem.activeRoutes.includes(name)
      ) {
        return true;
      }
      return name.includes(this.menuItem.toStateName);
    },

    computedClass() {
      // If active inbox is present, do not highlight conversations
      if (this.activeInbox) return ' ';
      if (
        this.isAllConversations ||
        this.isMentions ||
        this.isUnattended ||
        this.isCurrentRoute
      ) {
        return 'bg-woot-25 dark:bg-slate-800 text-woot-500 dark:text-woot-500 hover:text-woot-500 dark:hover:text-woot-500 active-view';
      }
      if (this.hasSubMenu) {
        if (
          this.isTeamsSettings ||
          this.isInboxSettings ||
          this.isIntegrationsSettings ||
          this.isApplicationsSettings
        ) {
          return 'bg-woot-25 dark:bg-slate-800 text-woot-500 dark:text-woot-500 hover:text-woot-500 dark:hover:text-woot-500 active-view';
        }
        return 'hover:text-slate-700 dark:hover:text-slate-100';
      }

      return 'hover:text-slate-700 dark:hover:text-slate-100';
    },
  },
  methods: {
    computedInboxClass(child) {
      const { type, phoneNumber } = child;
      if (!type) return '';
      const classByType = getInboxClassByType(type, phoneNumber);
      return classByType;
    },
    computedInboxErrorClass(child) {
      const { type, reauthorizationRequired } = child;
      if (!type) return '';
      const warningClass = getInboxWarningIconClass(
        type,
        reauthorizationRequired
      );
      return warningClass;
    },
    newLinkClick(e, navigate) {
      if (this.menuItem.newLinkRouteName) {
        navigate(e);
      } else if (this.menuItem.showModalForNewItem) {
        if (this.menuItem.modalName === 'AddLabel') {
          e.preventDefault();
          this.$emit('addLabel');
        }
      }
    },
    showItem(item) {
      return this.isAdmin && !!item.newLink;
    },
    onClickOpen() {
      this.$emit('open');
    },
    onLeafSelect() {
      // Navegar a un ítem plano de primer nivel cierra el grupo abierto.
      if (!this.hasSubMenu) {
        this.$emit('toggleGroup', null);
      }
    },
    toggleGroup() {
      if (this.isFoldable) {
        this.$emit('fold', this.menuItem.foldKey);
        return;
      }
      if (this.isCollapsibleGroup) {
        // Acordeón: si ya está abierto, cerrar (null); si no, abrir este.
        this.$emit(
          'toggleGroup',
          this.isExpanded ? null : this.menuItem.toState
        );
      }
    },
    showChildCount(count) {
      return Number.isInteger(count);
    },
  },
};
</script>

<template>
  <li
    v-show="isMenuItemVisible"
    class="mt-1"
    :class="{ 'pl-2 rtl:pl-0 rtl:pr-2': menuItem.nested }"
  >
    <div v-if="hasSubMenu" class="flex justify-between">
      <button
        v-if="hasHeaderToggle"
        type="button"
        class="flex items-center flex-grow gap-1 px-2 pt-1 my-2 text-sm font-semibold text-left bg-transparent border-0 cursor-pointer text-slate-700 dark:text-slate-200"
        @click="toggleGroup"
      >
        <fluent-icon
          :icon="headerChevron"
          size="12"
          class="text-slate-400 dark:text-slate-500"
        />
        {{ $t(`SIDEBAR.${menuItem.label}`) }}
        <span
          v-if="hasLabelCount"
          class="font-normal text-slate-400 dark:text-slate-500"
        >
          ({{ menuItem.labelCount }})
        </span>
      </button>
      <span
        v-else
        class="px-2 pt-1 my-2 text-sm font-semibold text-slate-700 dark:text-slate-200"
      >
        {{ $t(`SIDEBAR.${menuItem.label}`) }}
      </span>
      <div v-if="menuItem.showNewButton" class="flex items-center">
        <woot-button
          size="tiny"
          variant="clear"
          color-scheme="secondary"
          icon="add"
          class="p-0 ml-2"
          @click="onClickOpen"
        />
      </div>
    </div>
    <button
      v-else-if="menuItem.action"
      type="button"
      class="flex items-center w-full p-2 m-0 text-sm font-medium leading-4 text-left bg-transparent border-0 rounded-lg cursor-pointer text-slate-700 dark:text-slate-100 hover:bg-slate-25 dark:hover:bg-slate-800"
      @click="$emit('action', menuItem.action)"
    >
      <fluent-icon
        :icon="menuItem.icon"
        class="min-w-[1rem] mr-1.5 rtl:mr-0 rtl:ml-1.5"
        size="14"
      />
      {{ $t(`SIDEBAR.${menuItem.label}`) }}
    </button>
    <router-link
      v-else
      class="flex items-center p-2 m-0 text-sm font-medium leading-4 rounded-lg text-slate-700 dark:text-slate-100 hover:bg-slate-25 dark:hover:bg-slate-800"
      :class="computedClass"
      :to="menuItem && menuItem.toState"
      @click.native="onLeafSelect"
    >
      <fluent-icon
        :icon="menuItem.icon"
        class="min-w-[1rem] mr-1.5 rtl:mr-0 rtl:ml-1.5"
        size="14"
      />
      {{ $t(`SIDEBAR.${menuItem.label}`) }}
      <span
        v-if="showChildCount(menuItem.count)"
        class="px-1 py-0 mx-1 font-medium rounded-md text-xxs"
        :class="{
          'text-slate-300 dark:text-slate-600': isCountZero && !isActiveView,
          'text-slate-600 dark:text-slate-50': !isCountZero && !isActiveView,
          'bg-woot-75 dark:bg-woot-200 text-woot-600 dark:text-woot-600':
            isActiveView,
          'bg-slate-50 dark:bg-slate-700': !isActiveView,
        }"
      >
        {{ `${menuItem.count}` }}
      </span>
      <span
        v-if="menuItem.beta"
        data-view-component="true"
        label="Beta"
        class="inline-block px-1 mx-1 font-medium leading-4 text-green-500 border border-green-400 rounded-lg text-xxs"
      >
        {{ $t('SIDEBAR.BETA') }}
      </span>
    </router-link>

    <ul
      v-if="hasSubMenu && areChildrenVisible"
      class="mb-0 ml-0 list-none"
      :class="{ 'pl-3 rtl:pl-0 rtl:pr-3': hasHeaderToggle }"
    >
      <SecondaryChildNavItem
        v-for="child in shownChildren"
        :key="child.id"
        :to="child.toState"
        :label="child.translate ? $t(`SIDEBAR.${child.label}`) : child.label"
        :label-color="child.color"
        :should-truncate="child.truncateLabel"
        :icon="child.icon || computedInboxClass(child)"
        :warning-icon="computedInboxErrorClass(child)"
        :show-child-count="showChildCount(child.count)"
        :child-item-count="child.count"
      />
      <li v-if="hasChildLimit" class="pl-1">
        <woot-button
          size="tiny"
          variant="clear"
          color-scheme="secondary"
          :icon="showAllChildren ? 'chevron-up' : 'chevron-down'"
          @click="showAllChildren = !showAllChildren"
        >
          {{
            showAllChildren
              ? $t('SIDEBAR.SHOW_LESS')
              : $t('SIDEBAR.SHOW_MORE', { count: hiddenChildrenCount })
          }}
        </woot-button>
      </li>
      <Policy :permissions="['administrator']">
        <router-link
          v-if="menuItem.newLink"
          v-slot="{ href, navigate }"
          :to="menuItem.toState"
          custom
        >
          <li class="pl-1">
            <a :href="href">
              <woot-button
                size="tiny"
                variant="clear"
                color-scheme="secondary"
                icon="add"
                :data-testid="menuItem.dataTestid"
                @click="e => newLinkClick(e, navigate)"
              >
                {{ $t(`SIDEBAR.${menuItem.newLinkTag}`) }}
              </woot-button>
            </a>
          </li>
        </router-link>
      </Policy>
    </ul>
  </li>
</template>
